import type Lenis from 'lenis'

/**
 * Magnetic block titles by bending Lenis's destination.
 *
 * Lenis owns the scroll. While a wheel or touch gesture is in flight, the predicted resting point is Lenis's
 * `targetScroll`. When that point falls within magnet range of a block title, the destination is moved onto the
 * title (`targetScroll` and the running lerp's `to`). Lenis then eases there with no second scroll to start.
 * Duration-based programmatic scrolls (glides, nav, keys) are left alone.
 *
 * The only wheel interception is the optional `onBeforeStep` hook (the Services audience toggle in app.ts).
 */

export interface Stop {
  id: string
  /** Scroll position that docks the block title under the fixed header. */
  top: number
}

export interface StepContext {
  currentStop: Stop | undefined
  dir: 1 | -1
  y: number
}

export interface ScrollStopConfig {
  onBeforeStep?: (ctx: StepContext) => boolean
}

export interface ScrollStopController {
  measure: () => void
  getStop: (id: string) => number | undefined
  destroy: () => void
  glideTo: (top: number, duration?: number) => void
}

/** Lenis's internal lerp/duration animation. Not in the public typings, so it is read through this shape. */
interface LenisAnimate {
  isRunning: boolean
  lerp?: number
  duration?: number
  to: number
}

const WHEEL_IDLE_MS = 60 // wait briefly after the last wheel/touch tick so we do not fight an active flick
const CATCH_RATIO = 0.45 // magnet range around the predicted stop as a share of viewport height ...
const CATCH_MAX = 360 // ... capped in px
const TITLE_GAP = 12 // px between the fixed header and the block title once docked
const MIN_GAP = 2 // px: closer than this counts as already docked
const SNAP_SECONDS = 0.6
const KEY_EPSILON = 20 // px: stops this close to the current position are skipped by keyboard steps

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)

export function initScrollStop(
  lenis: Lenis,
  config: ScrollStopConfig = {}
): ScrollStopController {
  let stops: Stop[] = []
  let idleTimer = 0

  const animate = () => (lenis as unknown as { animate?: LenisAnimate }).animate

  const isActive = () =>
    window.innerWidth > 860 &&
    !window.matchMedia('(pointer: coarse)').matches &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const isLocked = () => document.documentElement.classList.contains('is-locked')

  const headerHeight = () => {
    const header = document.getElementById('site-header')
    return header ? header.offsetHeight : 76
  }

  const maxScroll = () => Math.max(0, document.documentElement.scrollHeight - window.innerHeight)

  // Dock point = the block's kicker / section head (the eyebrow above the h2), so the whole heading lands under the
  // header. Docking the h2 itself would hide the eyebrow behind the header. Measured in document space.
  const titleTopOf = (el: HTMLElement): number => {
    const title =
      el.querySelector<HTMLElement>('.kicker, .section-head') ??
      el.querySelector<HTMLElement>('h2') ??
      el
    return title.getBoundingClientRect().top + window.scrollY
  }

  // Measured at rest, not from the last resize: lazily built blocks above can move the titles after that.
  const measure = () => {
    const max = maxScroll()
    const clearance = headerHeight() + TITLE_GAP
    const items: Stop[] = []

    document.querySelectorAll<HTMLElement>('[data-section]').forEach((el) => {
      const id = el.dataset.section ?? ''
      if (id === 'home') {
        items.push({ id, top: 0 })
        return
      }
      const top = Math.round(Math.min(max, Math.max(0, titleTopOf(el) - clearance)))
      items.push({ id, top })
    })

    stops = items.sort((a, b) => a.top - b.top)
  }

  // Stops the predicted destination may dock to. Only stops ahead of the current position in the direction
  // of travel qualify, so a deliberate move past a title is not pulled back to it.
  const nearestStop = (predicted: number, travel: 1 | -1): (Stop & { d: number }) | null => {
    const range = Math.min(CATCH_MAX, window.innerHeight * CATCH_RATIO)
    const y = lenis.scroll
    let best: (Stop & { d: number }) | null = null
    for (const s of stops) {
      if ((s.top - y) * travel < -MIN_GAP) continue
      const d = Math.abs(s.top - predicted)
      if (d > range) continue
      if (!best || d < best.d) best = { ...s, d }
    }
    return best
  }

  // Bend the running glide's destination onto the nearest title. Guards: only Lenis's own lerp (wheel/touch)
  // glide is bent, and only while it is smooth-scrolling; native scrolls and duration-based scrollTo are left alone.
  const bendTarget = () => {
    idleTimer = 0
    if (!isActive() || isLocked()) return
    const anim = animate()
    if (!anim || !anim.isRunning || !anim.lerp || anim.duration) return
    if (lenis.isScrolling !== 'smooth') return

    // Re-read the titles from the live layout on every gesture. Stops measured at load can be stale: blocks
    // build and images load after it, and transforms or sticky offsets do not resize <body>, so no observer fires.
    measure()

    const predicted = lenis.targetScroll
    const travel = Math.sign(predicted - lenis.scroll) as 1 | -1 | 0
    if (travel === 0) return

    const stop = nearestStop(predicted, travel)
    if (!stop || stop.d < MIN_GAP) return

    // Lenis's lerp reads `to` every frame, so moving both values retargets the running glide without a new scroll.
    lenis.targetScroll = stop.top
    anim.to = stop.top
  }

  const onUserInput = () => {
    if (!isActive()) return
    window.clearTimeout(idleTimer)
    idleTimer = window.setTimeout(bendTarget, WHEEL_IDLE_MS)
  }

  // Duration-based glide for hook, keyboard and Home/End steps.
  const glideTo = (top: number, duration = SNAP_SECONDS) => {
    const clamped = Math.max(0, Math.min(maxScroll(), Math.round(top)))
    lenis.scrollTo(clamped, { duration, easing: easeOutCubic, lock: false, force: true })
  }

  const currentStop = (y: number) => stops.filter((s) => s.top <= y + 40).at(-1) ?? stops[0]

  // Wheel: observe only. A notch is swallowed solely when onBeforeStep claims it.
  const onWheel = (e: WheelEvent) => {
    if (!config.onBeforeStep || !isActive() || isLocked()) return
    const el = e.target as Element | null
    if (el?.closest?.('dialog, [data-lenis-prevent]')) return
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY) || e.deltaY === 0) return

    const y = Math.round(lenis.scroll)
    const dir: 1 | -1 = e.deltaY > 0 ? 1 : -1
    if (config.onBeforeStep({ currentStop: currentStop(y), dir, y })) {
      e.preventDefault()
      e.stopImmediatePropagation()
    }
  }

  const onKeyDown = (e: KeyboardEvent) => {
    if (!isActive() || isLocked()) return
    if (e.ctrlKey || e.metaKey || e.altKey) return
    const el = e.target as HTMLElement | null
    const tag = el?.tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable || el?.closest?.('dialog, [data-lenis-prevent]')) {
      return
    }

    const y = Math.round(lenis.scroll)
    const forward = e.key === 'PageDown' || e.key === 'j'
    const backward = e.key === 'PageUp' || e.key === 'k'

    if (forward || backward) {
      const dir: 1 | -1 = forward ? 1 : -1
      if (config.onBeforeStep?.({ currentStop: currentStop(y), dir, y })) {
        e.preventDefault()
        return
      }
      const target = forward
        ? stops.find((s) => s.top > y + KEY_EPSILON)
        : stops.filter((s) => s.top < y - KEY_EPSILON).at(-1)
      if (target) {
        e.preventDefault()
        glideTo(target.top)
      }
    } else if (e.key === 'Home') {
      e.preventDefault()
      glideTo(0)
    } else if (e.key === 'End') {
      e.preventDefault()
      glideTo(maxScroll())
    }
  }

  // Stops are measured once the first frame has laid out; `load` covers images and fonts above the fold.
  const frameId = requestAnimationFrame(measure)
  window.addEventListener('resize', measure, { passive: true })
  window.addEventListener('load', measure, { passive: true })
  // Blocks are built in turns after boot, so stop positions change: re-measure when the body resizes.
  const resizeObserver = typeof ResizeObserver !== 'undefined' && document.body ? new ResizeObserver(measure) : null
  resizeObserver?.observe(document.body)

  window.addEventListener('wheel', onUserInput, { passive: true })
  window.addEventListener('touchmove', onUserInput, { passive: true })
  window.addEventListener('wheel', onWheel, { passive: false, capture: true })
  window.addEventListener('keydown', onKeyDown)

  const getStop = (id: string): number | undefined => stops.find((s) => s.id === id)?.top

  const destroy = () => {
    window.clearTimeout(idleTimer)
    cancelAnimationFrame(frameId)
    window.removeEventListener('resize', measure)
    window.removeEventListener('load', measure)
    window.removeEventListener('wheel', onUserInput)
    window.removeEventListener('touchmove', onUserInput)
    window.removeEventListener('wheel', onWheel, { capture: true })
    window.removeEventListener('keydown', onKeyDown)
    resizeObserver?.disconnect()
  }

  return { measure, getStop, destroy, glideTo }
}
